<script setup>
import { until } from '@vueuse/core'
import {
  useBreakpoints,
  useCalendar,
  useDataQueryStore,
} from '@daysmart/daysmart-vue-components/composables'
import { safeIsEqual } from '@daysmart/daysmart-vue-components/utils'
import { useSelectedFamilyMemberState } from '@/core/composables/useSelectedFamilyMemberState'
import { useCalendarViewState } from '@/schedule/composables/useCalendarViewState'
import { useCalendarBus } from '@/schedule/composables/useCalendarBus'
import { useCalendarDateState } from '@/schedule/composables/useCalendarDateState'
import { useCalendarSettings } from '@/schedule/composables/useCalendarSettings'
import { useScheduleQuery } from '@/schedule/composables/useScheduleQuery'

defineOptions({
  inheritAttrs: false,
})

const { getFeatureRouteVisibilityByKey } = useFeatureRoutes()

const calendarBus = useCalendarBus()
const { customFirstDayOfWeek } = useCalendarSettings()
const calendar = useCalendar({ customFirstDayOfWeek })
const params = useSearchParams()

const api = useApi()
const {
  hasFamily,
  sessionData,
  currentProviderLogoUrl,
  currentProviderName,
  isAuthenticated,
  hasCustomerId,
} = useSessionData()
const view = useCalendarViewState()
const selectedFamilyMember = useSelectedFamilyMemberState()
const { current: currentBreakpoint } = useBreakpoints()
const today = calendar.today
const date = useCalendarDateState()

const notAuthenticatedModal = ref(null)

const { actions: queryActions, state: queryState }
  = useDataQueryStore(
    'calendar-store',
    {
      enablePaging: false,
      initialState: {
        filters: {
          venues: [],
          category: [],
          instructors: [],
          offering_types: [],
          age_restrictions: [],
        },
      },
      filterParams: {
        venues: Array,
        category: Array,
        instructors: Array,
        offering_types: Array,
        age_restrictions: Array,
      },
      urlSearchParams: params,
    },
  )

const filterForm = ref(queryState.value.filters || {})

// modal stuff
const authenticatedModal = ref(null)
const isEventModalOpen = ref(false)
function onEventModalShow() {
  isEventModalOpen.value = true
}
function onEventModalHide() {
  isEventModalOpen.value = false
  params.e = undefined
}

const selectedEventId = ref()
const selectedEvent = ref()

calendarBus.on('calendar-event-selected', showModalOnEventSelect)

watch(
  () => params.e,
  (value) => {
    if (+value !== selectedEventId.value)
      selectedEventId.value = value ? +value : undefined
  },
  { immediate: true },
)
watch(
  () => selectedEventId.value,
  (value) => {
    if (+value !== params.e)
      params.e = value
  },
)

function showModalOnEventSelect(id) {
  if (!id)
    return

  selectedEventId.value = +id
  selectedEvent.value = calendarData.value?.events?.find(
    event => event.id === selectedEventId.value,
  )

  // if (isAuthenticated && hasCustomerId)
  nextTick(() => authenticatedModal.value?.show())
  // else notAuthenticatedModal.value?.show()
}

async function highlightToday() {
  await until(calendarLoading).toBe(false)
  calendarBus.emit('highlight', today.value?.format('YYYY-MM-DD'))
}

function selectDate(value) {
  if (!value)
    return
  // if value is "YYYY-MM" - ie, selecting a prev/next month
  if (value.length === 7)
    date.value = `${value}-01`
  else date.value = value
  params.date = date.value
}

const viewDateArray = computed(() => {
  if (view.value === 'month')
    return calendar.getMonthView.value(date.value)
  if (view.value === 'week' || view.value === 'day')
    return calendar.getWeekView.value(date.value)
  return calendar.getDayView(date.value)
})

const viewDateRange = computed(() => ({
  start: `${viewDateArray.value[0].format('YYYY-MM-DD')}`,
  end: `${viewDateArray.value.at(-1).format('YYYY-MM-DD')}`,
}))

watch(filterForm, (value) => {
  queryActions.setFilters(value)
})

watch(
  viewDateRange,
  (value, prevValue) => {
    if (safeIsEqual(value, prevValue))
      return
    // loading.value = true // reduce flicker
    queryActions.setDateRange(value)
  },
  { immediate: true },
)

watch(
  currentBreakpoint,
  () => {
    if (currentBreakpoint.value === 'xs')
      view.value = 'day'
  },
  { immediate: true },
)

onMounted(async () => {
  highlightToday()

  if (params.date)
    selectDate(params.date)

  if (params.c) {
    if (hasFamily)
      selectedFamilyMember.value = +params.c
    params.c = undefined
  }

  if (params.e)
    showModalOnEventSelect(params.e)
})

const queryEnabled = computed(() => params.e === undefined)

const {
  data: calendarData,
  isPending: calendarLoading,
  refetch: refetchCalendarData,
} = useScheduleQuery({
  queryState,
  today,
  enabled: queryEnabled,
})

calendarBus.on('refresh', refetchCalendarData)

const eventDates = computed(() => {
  return calendarData.value?.events
    ? [...new Set(calendarData.value.events.map(event => event.starts_at))]
    : []
})

const eventVenueTimezones = computed(() => {
  return calendarData.value?.events
    ? [...new Set(calendarData.value.events.map(event => event.venue.timezone))]
    : []
})
</script>

<template>
  <PageContainer class="mx-4 flex py-4">
    <ScheduleAppointmentButton
      v-if="getFeatureRouteVisibilityByKey('appointments') && sessionData?.appointments?.has_visible_types"
    />
    <div class="flex justify-between flex-gap-4 px-4 m-block-4 md:(flex-row items-center) lt-md:flex-col">
      <SelectFamilyMemberDropdown />
      <ScheduleFiltersMobileButton />
    </div>

    <ScheduleViewStickyHeader
      :date="date"
      :event-dates="eventDates"
      :event-venue-timezones="eventVenueTimezones"
      @select-date="selectDate"
    />

    <DSLoadingWrapper
      class="relative min-h-200px"
      :loading="calendarLoading"
    >
      <ScheduleMonthView
        v-if="view === 'month'"
        class="flex flex-col bg-white p-4 pt-8"
        :events="calendarData.events"
        :closed-dates="calendarData.closedDates"
        :date="date"
      />
      <ScheduleWeekView
        v-if="view === 'week'"
        class="flex flex-col bg-white p-4 pt-8"
        :events="calendarData.events"
        :closed-dates="calendarData.closedDates"
        :date="date"
      />
      <ScheduleListView
        v-if="view === 'day'"
        class="flex flex-col bg-white p-4 pt-8"
        :events="calendarData.events"
        :closed-dates="calendarData.closedDates"
        :date="date"
        :date-array="viewDateArray"
        @select-date="selectDate"
      />
    </DSLoadingWrapper>
  </PageContainer>
  <ScheduleFilters v-model="filterForm" />
  <ScheduleBookingModalEventDetails
    ref="authenticatedModal"
    :initial-event="selectedEvent"
    :initial-event-id="selectedEventId"
    @show="onEventModalShow"
    @show-unauthenticated="notAuthenticatedModal?.show"
    @hide="onEventModalHide"
  />
  <ScheduleBookingModalLoginSignup
    ref="notAuthenticatedModal"
    :provider="currentProviderName"
    :logo="currentProviderLogoUrl"
    @hide="params.e = undefined"
  />
</template>

<i18n locale="de" lang="json">
{
  "All times {timezone}": "Jederzeit {timezone}"
}
</i18n>

<i18n locale="en-us" lang="json">
{
  "All times {timezone}": "All times {timezone}"
}
</i18n>

<i18n locale="es" lang="json">
{
  "All times {timezone}": "Todos los horarios {timezone}"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "All times {timezone}": "Toutes les heures {timezone}"
}
</i18n>
